/**
 * ProseMirror JSONContent → Markdown serializer.
 *
 * Converts the rich content inside a section node (paragraphs, headings,
 * lists, code blocks, blockquotes) into a Markdown string. This is used
 * to populate typed Convex columns with Markdown on every save.
 *
 * Structured field atoms are extracted by schema-driven field helpers.
 * This module only handles rich-text content.
 */

import type { JSONContent } from "@tiptap/react";
import { serializeDocumentEvidenceToken } from "@/lib/document-evidence-link";
import { parseMentionVersionRef, serializeMessageMentionToken } from "@/lib/message-mention-link";
import {
  renderCatalogInlineText,
  renderCatalogTemplateMarkdown,
} from "../../../../../../packages/shared/src/catalog/embed-rendering";

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Serialize an array of block-level ProseMirror nodes to a Markdown string.
 *
 * Takes the `content` array from inside a section node (richSection or
 * paragraphSection) and produces clean Markdown.
 */
export type MarkdownSerializationOptions = {
  documentEvidence?: "markdown" | "label";
};

export function serializeToMarkdown(
  nodes: JSONContent[],
  options: MarkdownSerializationOptions = {}
): string {
  if (!nodes || nodes.length === 0) return "";
  return nodes.map((node) => serializeBlock(node, options)).join("");
}

// ─── Block-Level Serialization ───────────────────────────────────────────────

function serializeBlock(node: JSONContent, options: MarkdownSerializationOptions): string {
  switch (node.type) {
    case "paragraph":
      return serializeParagraph(node, options);

    case "heading":
      return serializeHeading(node, options);

    case "bulletList":
      return `${serializeList(node, "bullet", options)}\n`;

    case "orderedList":
      return `${serializeList(node, "ordered", options)}\n`;

    case "codeBlock":
      return serializeCodeBlock(node);

    case "blockquote":
      return serializeBlockquote(node, options);

    case "horizontalRule":
      return "---\n\n";

    case "dataCatalogTemplateEntry":
      return renderCatalogTemplateMarkdown({ ...(node.attrs ?? {}), embedKind: "template" });

    default:
      // Unknown block node — try to extract text as fallback
      return node.content ? serializeToMarkdown(node.content, options) : "";
  }
}

function serializeParagraph(node: JSONContent, options: MarkdownSerializationOptions): string {
  const text = serializeInline(node.content, options);
  return `${text}\n\n`;
}

function serializeHeading(node: JSONContent, options: MarkdownSerializationOptions): string {
  const level = (node.attrs?.level as number) ?? 2;
  const prefix = "#".repeat(level);
  const text = serializeInline(node.content, options);
  return `${prefix} ${text}\n\n`;
}

function serializeList(
  node: JSONContent,
  type: "bullet" | "ordered",
  options: MarkdownSerializationOptions,
  indent = 0
): string {
  if (!node.content) return "";

  const lines: string[] = [];
  const indentStr = "  ".repeat(indent);

  for (let i = 0; i < node.content.length; i++) {
    const item = node.content[i] as JSONContent;
    if (item.type !== "listItem") continue;

    const prefix = type === "bullet" ? `${indentStr}- ` : `${indentStr}${i + 1}. `;

    if (!item.content) {
      lines.push(prefix);
      continue;
    }

    const parts: string[] = [];
    for (const child of item.content) {
      if (child.type === "paragraph") {
        parts.push(serializeInline(child.content, options));
      } else if (child.type === "bulletList") {
        parts.push(`\n${serializeList(child, "bullet", options, indent + 1)}`);
      } else if (child.type === "orderedList") {
        parts.push(`\n${serializeList(child, "ordered", options, indent + 1)}`);
      } else {
        // Other block content inside list item
        parts.push(serializeBlock(child, options).trimEnd());
      }
    }

    // First part gets the list marker, additional paragraphs get continuation indent
    const [first, ...rest] = parts;
    lines.push(prefix + (first ?? ""));
    for (const part of rest) {
      if (part.startsWith("\n")) {
        // Nested list — already has its own formatting
        lines.push(part);
      } else {
        lines.push(`${indentStr}  ${part}`);
      }
    }
  }

  return `${lines.join("\n")}\n`;
}

function serializeCodeBlock(node: JSONContent): string {
  const language = (node.attrs?.language as string) ?? "";
  const text = extractPlainText(node);
  return `\`\`\`${language}\n${text}\n\`\`\`\n\n`;
}

function serializeBlockquote(node: JSONContent, options: MarkdownSerializationOptions): string {
  if (!node.content) return "> \n\n";

  const inner = node.content
    .map((child) => {
      const blockText = serializeBlock(child, options).trimEnd();
      // Prefix every line with > for proper Markdown blockquote
      return blockText
        .split("\n")
        .map((line) => `> ${line}`)
        .join("\n");
    })
    .join("\n");

  return `${inner}\n\n`;
}

// ─── Inline Serialization ────────────────────────────────────────────────────

/**
 * Serialize inline content (text with marks, mentions, hard breaks) to Markdown.
 */
export function serializeInline(
  content?: JSONContent[],
  options: MarkdownSerializationOptions = {}
): string {
  if (!content) return "";

  return content
    .map((node) => {
      switch (node.type) {
        case "text":
          return serializeTextWithMarks(node);

        case "mention":
          return serializeMention(node);

        case "documentEvidence":
          return serializeDocumentEvidence(node, options);

        case "dataCatalogEntry":
          return renderCatalogInlineText(node.attrs ?? {});

        case "hardBreak":
          return "  \n";

        default:
          return "";
      }
    })
    .join("");
}

function serializeDocumentEvidence(
  node: JSONContent,
  options: MarkdownSerializationOptions
): string {
  const label =
    typeof node.attrs?.label === "string" && node.attrs.label.trim()
      ? node.attrs.label.trim()
      : "Document evidence";
  if (options.documentEvidence === "label") {
    return label;
  }
  const startChar = typeof node.attrs?.startChar === "number" ? node.attrs.startChar : undefined;
  const endChar = typeof node.attrs?.endChar === "number" ? node.attrs.endChar : undefined;
  return serializeDocumentEvidenceToken({
    kind: "document_block",
    artifactId: typeof node.attrs?.artifactId === "string" ? node.attrs.artifactId : "",
    processingRevisionId:
      typeof node.attrs?.processingRevisionId === "string" ? node.attrs.processingRevisionId : "",
    blockId: typeof node.attrs?.blockId === "string" ? node.attrs.blockId : "",
    ...(startChar !== undefined && endChar !== undefined ? { span: { startChar, endChar } } : {}),
    label,
  });
}

function serializeMention(node: JSONContent): string {
  const id = typeof node.attrs?.id === "string" ? node.attrs.id : "";
  const label =
    typeof node.attrs?.label === "string" ? node.attrs.label : typeof id === "string" ? id : "";
  const mentionType = typeof node.attrs?.mentionType === "string" ? node.attrs.mentionType : "";

  if (!id || !mentionType) {
    const fallbackLabel = label || id;
    return fallbackLabel.startsWith("@") ? fallbackLabel : `@${fallbackLabel}`;
  }

  return serializeMessageMentionToken({
    id,
    mentionType,
    label,
    versionIntent: parseMentionVersionRef(
      typeof node.attrs?.versionRef === "string" ? node.attrs.versionRef : null
    ),
  });
}

/**
 * Split a text node into its boundary whitespace and the content between it.
 *
 * Mirrors `splitBoundaryWhitespace` in the Convex serializers
 * (`packages/convex/convex/assistant/domain/richTextMarkdown.ts`) — the two
 * markdown pipelines share no code.
 */
function splitBoundaryWhitespace(text: string): {
  leading: string;
  content: string;
  trailing: string;
} {
  const [, leading = "", content = "", trailing = ""] = /^(\s*)([\s\S]*?)(\s*)$/.exec(text) ?? [];
  return { leading, content, trailing };
}

/**
 * Apply Markdown formatting for inline marks (bold, italic, code, strike,
 * highlight, link).
 *
 * Marks are applied from outermost to innermost. When multiple marks overlap,
 * the wrapping order is: strike > bold > italic > code (innermost).
 *
 * The emphasis-style delimiters (`**`, `*`, `~~`, `==`) are whitespace
 * sensitive: markdown-it never treats a delimiter adjacent to whitespace as an
 * opener or closer, so `** TODO **` re-parses as literal asterisks and drops the
 * mark. The node's boundary whitespace is therefore hoisted outside every
 * delimiter once, up front — before any wrapper runs — so it stays outside no
 * matter how many marks nest. Content that is empty or all whitespace has
 * nothing for the delimiters to attach to and is emitted unwrapped.
 *
 * Code spans and links are not whitespace sensitive and carry their own
 * boundary semantics, so the whitespace is re-attached (flushed) before those
 * wrappers run — a link keeps linking exactly the text it linked before.
 *
 * Every highlight color collapses to plain `==text==` — the syntax carries no
 * color, so a round-trip through Markdown returns the default yellow.
 */
function serializeTextWithMarks(node: JSONContent): string {
  const original = node.text ?? "";
  if (!original) return "";

  const marks = node.marks ?? [];
  if (marks.length === 0) return original;

  const { leading, content, trailing } = splitBoundaryWhitespace(original);
  let text = content;
  let pendingLeading = leading;
  let pendingTrailing = trailing;
  const flushBoundaryWhitespace = () => {
    text = `${pendingLeading}${text}${pendingTrailing}`;
    pendingLeading = "";
    pendingTrailing = "";
  };

  // Apply marks — order matters for nesting
  for (const mark of marks) {
    switch (mark.type) {
      case "code":
        flushBoundaryWhitespace();
        text = `\`${text}\``;
        break;
      case "italic":
        if (text) text = `*${text}*`;
        break;
      case "bold":
        if (text) text = `**${text}**`;
        break;
      case "strike":
        if (text) text = `~~${text}~~`;
        break;
      case "highlight":
        if (text) text = `==${text}==`;
        break;
      case "link":
        flushBoundaryWhitespace();
        text = `[${text}](${(mark.attrs?.href as string) ?? ""})`;
        break;
    }
  }

  return `${pendingLeading}${text}${pendingTrailing}`;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Recursively extract plain text from a node tree (no marks, no formatting). */
function extractPlainText(node: JSONContent): string {
  if (node.text) return node.text;
  if (!node.content) return "";
  return node.content.map(extractPlainText).join("");
}
